import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { IgButton, IgDialog, IgTypography } from "@intelligems/ig-theme";
import {
  ExperienceListVariationDto,
  GenericExperienceFe,
} from "@intelligems/ig-types";
import { Grid } from "@mui/material";
import { useFileBuild } from "@src/features/experiments/hooks/use-file-build";

import { isDialogOpen, setDialog } from "../../../../store/slices/global";
import { VariationButtons } from "../VariationButtons";

interface DownloadPriceCsvDialogProps {
  currentExperience: GenericExperienceFe;
  variations: (Omit<ExperienceListVariationDto, "experienceId"> & {
    experienceId?: string;
  })[];
}

export const DownloadPriceCsvDialog = ({
  currentExperience,
  variations,
}: DownloadPriceCsvDialogProps) => {
  const DIALOG_NAME = "downloadPriceCsv";
  const dispatch = useDispatch();
  const isOpen = useSelector(isDialogOpen(DIALOG_NAME));
  const { buildPriceCsvFile, downloadTemplateReady } = useFileBuild({
    experience: currentExperience,
  });
  const [selectedTestGroup, setSelectedTestGroup] = useState<
    string | undefined
  >(undefined);

  const onDownloadClick = () => {
    const currentVariation = variations.find(
      (tg) => tg.id === selectedTestGroup
    );
    let tempVariations: (Omit<ExperienceListVariationDto, "experienceId"> & {
      experienceId?: string;
    })[] = [];
    if (currentVariation) {
      tempVariations = [currentVariation];
    }
    buildPriceCsvFile.mutate({
      body: {
        variations: tempVariations,
        selectedProducts: [],
        name: currentExperience.name,
        experienceId: currentExperience.id!,
        forExternalPriceUpload: true,
      },
      pathParams: {
        type: "csv",
      },
      queryParams: undefined,
    });

    closeDialog();
  };

  const closeDialog = () => {
    dispatch(setDialog({ dialog: DIALOG_NAME, open: false }));
  };

  const selectedCard = (id: string): boolean => {
    return selectedTestGroup === id;
  };

  return (
    <IgDialog
      title={"Download Price CSV"}
      handleClose={closeDialog}
      showDialog={isOpen}
      aria-labelledby="pricing-dialog"
      aria-describedby="pricing-dialog"
      onClose={closeDialog}
      // maxWidth={"xs"}
      rawContent={
        <Grid container direction={"column"} rowSpacing={2}>
          <Grid item>
            <IgTypography>
              Optional - Choose a test group for the prices. If none selected,
              max will be set.
            </IgTypography>
          </Grid>
          <Grid item>
            <VariationButtons
              selectedTestGroup={selectedTestGroup}
              setSelectedTestGroup={setSelectedTestGroup}
              selectedCard={selectedCard}
              variations={variations}
            />
          </Grid>
          <Grid
            container
            columnSpacing={2}
            direction={"row"}
            justifyContent={"flex-end"}
            item
          >
            <Grid item>
              <IgButton
                color={"secondary"}
                loading={false}
                onClick={closeDialog}
              >
                Cancel
              </IgButton>
            </Grid>
            <Grid item>
              <IgButton
                variant={"contained"}
                igVariant={
                  downloadTemplateReady.isReady ? "success" : undefined
                }
                loading={buildPriceCsvFile.isPending}
                onClick={() => {
                  if (
                    downloadTemplateReady.isReady &&
                    downloadTemplateReady.href
                  ) {
                    window.open(downloadTemplateReady.href, "_blank");
                  } else {
                    onDownloadClick();
                  }
                }}
              >
                {downloadTemplateReady.isReady
                  ? "CSV Ready - Click to Download"
                  : "Download CSV"}
              </IgButton>
            </Grid>
          </Grid>
        </Grid>
      }
      hideActions={true}
    />
  );
};

export default DownloadPriceCsvDialog;
